import NiceModal from '@ebay/nice-modal-react';
import PropTypes from 'prop-types';

import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import Status from 'components/field/Status';
import SwitchInput from 'components/field/SwitchInput';
import { NonFormContainer } from 'components/form/FormLayout.styled';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import LottiePlayer from 'components/global/LottiePlayer';
import Popper from 'components/global/Popper';
import Skeleton from 'components/global/Skeleton';
import SubformHeader from 'components/global/SubformHeader';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import SynchronizeBranchModal from 'pages/Products/Products//SynchronizeBranchModal';
import SynchronizeProductModal from 'pages/Products/Products/SynchronizeProductsModal';
import SynchronizeForm from 'pages/Shipments/ActionForms/SynchronizeForm';

import { SALES_CHANNELS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { PLATFORM } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import {
	faChain,
	faDiagramSubtask,
	faGear,
	faPenToSquare,
	faRotate,
	faStore,
	faTrashCan
} from '@fortawesome/pro-light-svg-icons';
import { faClock, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';

import StatusMappingForm from '../../Shipments/ActionForms/StatusMappingForm';
import ConnectSalesChannelModal from '../ConnectSalesChannelModal';
import DeleteStore from '../Stores/DeleteStore/DeleteStore';
import InventorySyncSettingsForm from '../Stores/StoreInventorySync/InventorySyncSettingsForm';
import StoreLocationForm from '../Stores/StoreLocation/StoreLocationForm';
import { setSalesChannel } from '../salesChannelDetailsSlice';
import useSallaSync from '../useSallaSync';
import { setConnectedStores } from './ConnectedStores.slice';
import * as S from './ConnectedStores.styled';
import SallaSyncOrderDateForm from './SallaSyncOrderDateForm';

const ConnectedStores = () => {
	// Hooks and states declaration

	const navigate = useNavigate();
	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const connectedStoresList = useSelector((state) => state?.connectedStores);
	const dispatch = useDispatch();
	const { showError } = useAlert();
	const [loading, setLoading] = useState(true);
	const [searchValue, setSearchValue] = useState(null);
	const debouncedValue = useDebounce(searchValue, 500);

	const getStores = async () => {
		try {
			setLoading(true);
			const response = await axiosPrivate.post(SALES_CHANNELS.GET_SALE_CHANNEL_BY_COMPANY);
			if (response.status === 200) {
				dispatch(setConnectedStores(response.data?.reverse()));
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getStores();
	}, []);

	const filteredList = useMemo(() => {
		if (!debouncedValue) return connectedStoresList;

		return connectedStoresList?.filter((item) => {
			const searchTerms = [item.channelName, item.storeName, t(`salesChannel.${item.integrationName}`)].map(
				(term) => term?.toLowerCase()
			);

			const searchValue = debouncedValue.toLowerCase();
			return searchTerms.some((term) => term?.includes(searchValue));
		});
	}, [debouncedValue, connectedStoresList]);

	return (
		<NonFormContainer>
			<SubformHeader
				boldTitle="connectedSalesChannels"
				lightTitle="connectedSalesChannelsDescription"
				className="table-lord-icon"
				trigger="hover"
			/>

			<S.InputContainer>
				<SearchInput setValue={setSearchValue} value={searchValue} placeholder="searchSalesChannel" fullWidth />
			</S.InputContainer>

			{loading ? (
				<S.PartnersContainer>
					{[...Array(5)].map((_, i) => (
						<S.LoadingRowSpaced key={i}>
							<S.LoadingRowCentered>
								<Skeleton width="7rem" height="7rem" radius={6} />
								<S.LoadingContent>
									<Skeleton width="12rem" height="2rem" />
									<S.LoadingMetaWrapper>
										<Skeleton width="7rem" height="1.6rem" />
										<Skeleton width="9rem" height="1.6rem" />
									</S.LoadingMetaWrapper>
								</S.LoadingContent>
							</S.LoadingRowCentered>

							<S.LoadingRowCenteredActions>
								<Skeleton width="4rem" height="2.4rem" radius={12} />
								<S.Separator />
								<Skeleton width="4rem" height="4rem" radius="50%" />
							</S.LoadingRowCenteredActions>
						</S.LoadingRowSpaced>
					))}
				</S.PartnersContainer>
			) : (
				<>
					{filteredList?.length ? (
						<S.PartnersContainer className="listing-container">
							{filteredList?.map((data) => (
								<StoreBox key={data.ID} store={data} />
							))}
						</S.PartnersContainer>
					) : (
						<S.PartnersContainer className="mt-5">
							<LottiePlayer name="sales-channels-connected-empty" width="auto" height="300px" />
							<div style={{ textAlign: 'center' }}>
								<Typography className="emptySubtitle" noWrap>
									emptyConnectedSalesChannelsSubtitle
								</Typography>
								<Button
									style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
									icon={faChain}
									label="connectNow"
									className="mb-3"
									onClick={() => navigate(mergePaths(PATHS.SALES_CHANNELS, PATHS.ONLINE_STORES))}
								/>
							</div>
						</S.PartnersContainer>
					)}
				</>
			)}
		</NonFormContainer>
	);
};

const StoreBox = ({ store }) => {
	// Hooks and states declaration
	const {
		ID,
		storeName,
		saleChannelID,
		slug,
		integrationName,
		active,
		icon,
		statusMappingUpdateTypes,
		statusMapping,
		storeLocation,
		syncBranches,
		syncOrders,
		syncProducts,
		merchantId
	} = store;
	const { t } = useTranslation();
	const { getLocaleDateWithTime } = useLocale();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const isAuthorized = useAccessControl();
	const navigate = useNavigate();
	const {
		syncOrders: syncSallaOrders,
		isLoading: sallaSyncLoading,
		restartAutomation,
		reConnectSalla
	} = useSallaSync();
	const sallaStore = useSelector((state) => state?.salla);

	const connectedStoresList = useSelector((state) => state?.connectedStores);

	const [menuButtonRef, setMenuButtonRef] = useState(null);

	const storeSyncInfo = useMemo(() => {
		return sallaStore?.syncInfo?.find?.((item) => item.salesChannelId === ID);
	}, [sallaStore?.syncInfo, ID]);

	const storeLastError = useMemo(() => {
		return sallaStore?.lastError?.find?.((item) => item?.salesChannelId === ID);
	}, [sallaStore?.lastError, ID]);

	const statusChange = async () => {
		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.UPDATE_SALE_CHANNEL_STATUS, {
				accountID: ID,
				status: !active
			});
			if (response.status === 200) {
				dispatch(
					setConnectedStores(
						connectedStoresList.map((store) => (store.ID === ID ? { ...store, active: !active } : store))
					)
				);
				showSuccess();
				if (
					(slug === 'salla-automation' && import.meta.env.VITE_PLATFORM === PLATFORM.desktop) ||
					slug === 'salla-desktop'
				) {
					restartAutomation();
				}
				if (response.data.sallaAppUrl) {
					window.open(response.data.sallaAppUrl, '_blank');
				}
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.F014') {
				navigate(PATHS.OTO_MODULES);
				showError({
					header: 'info.limitedShippingPartnersTitle',
					message: 'info.limitedShippingPartnersDescription'
				});
			} else {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			}
		}
	};

	const handleClick = (e, closePopper, form) => {
		e.stopPropagation();
		if (form) {
			e.preventDefault();
			NiceModal.show(form, {
				credentialsID: ID,
				enumValue: statusMappingUpdateTypes,
				storeName,
				salesChannel: store
			});
		}
		closePopper();
	};

	const openEditModal = (closePopper) => {
		dispatch(setSalesChannel(store));
		NiceModal.show(ConnectSalesChannelModal, {
			ID,
			salesChannelID: saleChannelID
		});
		closePopper();
	};

	const handleRemove = (closePopper) => {
		NiceModal.show(DeleteStore, { salesChannel: store });
		closePopper();
	};

	const handleSyncNow = async () => {
		const res = await syncSallaOrders({
			merchantId,
			salesChannelId: ID
		});
		if (res?.status === 200) {
			showSuccess();
		}
	};

	const handleSyncWithDate = async (closePopper) => {
		NiceModal.show(SallaSyncOrderDateForm, { merchantId, salesChannelId: ID });
		closePopper();
	};

	return (
		<S.StoreBoxContainer>
			<S.RowSpaced>
				<S.RowCentered>
					<Image width="7rem" height="7rem" url={icon} objectFit="scale-down" radius={6} />
					<S.StoreInfo>
						<S.IntegrationName>
							<Typography noWrap={true} variant={isMobile ? 'p3' : 'p1'} fontWeight="bold">
								{t(`salesChannel.${integrationName}`)}
							</Typography>

							<Status
								value={active ? 'active' : 'inactive'}
								style={{
									height: 'unset',
									padding: '0 var(--padding-1)'
								}}
							/>
						</S.IntegrationName>
						<S.StoreName>
							<Typography color="var(--content-3)" noWrap>
								<Trans
									i18nKey="connectedStoreName"
									values={{
										storeName
									}}
									components={{
										storeName: <Typography fontWeight="500" noWrap style={{ display: 'inline' }} />
									}}
								/>
							</Typography>
						</S.StoreName>
					</S.StoreInfo>
				</S.RowCentered>

				<S.RowCenteredActions>
					{slug === 'salla-automation' && active && import.meta.env.VITE_PLATFORM === PLATFORM.desktop && (
						<Button
							label="syncNow"
							variant="outline"
							size="small"
							onClick={handleSyncNow}
							icon={faRotate}
							loading={sallaSyncLoading}
						/>
					)}
					<SwitchInput value={active} setValue={statusChange} />
					<S.Separator />
					<Button
						ref={setMenuButtonRef}
						icon={faEllipsis}
						variant="transparent"
						size="large"
						iconSize="medium"
						iconButton
						color="var(--black)"
					/>

					{menuButtonRef && (
						<Popper refElement={menuButtonRef} timeout={100}>
							{(closePopper) => (
								<S.Menu id="rowMenuContainer">
									{isMobile && (
										<Typography variant="p1" className="px-4" fontWeight="500">
											moreActions
										</Typography>
									)}
									<S.MenuItem
										onContextMenu={() => openEditModal(closePopper)}
										onClick={() => openEditModal(closePopper)}>
										<Icon icon={faPenToSquare} />
										<Typography>editCredentials</Typography>
									</S.MenuItem>
									{storeLocation && (
										<S.MenuItem
											onContextMenu={(e) => handleClick(e, closePopper, StoreLocationForm)}
											onClick={(e) => handleClick(e, closePopper, StoreLocationForm)}>
											<Icon icon={faStore} />
											<Typography>matchPickupLocations</Typography>
										</S.MenuItem>
									)}

									{isAuthorized('PIMModule') && (
										<S.MenuItem
											onContextMenu={(e) =>
												handleClick(e, closePopper, InventorySyncSettingsForm)
											}
											onClick={(e) => handleClick(e, closePopper, InventorySyncSettingsForm)}>
											<Icon icon={faGear} />
											<Typography>inventorySyncSettings</Typography>
										</S.MenuItem>
									)}

									{active &&
										import.meta.env.VITE_PLATFORM === PLATFORM.desktop &&
										['salla-automation', 'salla-desktop'].includes(slug) && (
											<S.MenuItem
												onContextMenu={() => handleSyncWithDate(closePopper)}
												onClick={() => handleSyncWithDate(closePopper)}>
												<Icon icon={faRotate} />
												<Typography>syncOrdersWithDate</Typography>
											</S.MenuItem>
										)}

									{syncOrders && active && (
										<S.MenuItem
											onContextMenu={(e) => handleClick(e, closePopper, SynchronizeForm)}
											onClick={(e) => handleClick(e, closePopper, SynchronizeForm)}>
											<Icon icon={faRotate} />
											<Typography>syncOrder</Typography>
										</S.MenuItem>
									)}
									{syncProducts && (
										<S.MenuItem
											onContextMenu={(e) => handleClick(e, closePopper, SynchronizeProductModal)}
											onClick={(e) => handleClick(e, closePopper, SynchronizeProductModal)}>
											<Icon icon={faRotate} />
											<Typography>syncProducts</Typography>
										</S.MenuItem>
									)}

									{syncBranches && (
										<S.MenuItem
											onContextMenu={(e) => handleClick(e, closePopper, SynchronizeBranchModal)}
											onClick={(e) => handleClick(e, closePopper, SynchronizeBranchModal)}>
											<Icon icon={faRotate} />
											<Typography>syncBranches</Typography>
										</S.MenuItem>
									)}

									<Authorization path="/salesChannel/statusMapping">
										{statusMapping && (
											<S.MenuItem
												onContextMenu={(e) => handleClick(e, closePopper, StatusMappingForm)}
												onClick={(e) => handleClick(e, closePopper, StatusMappingForm)}>
												<Icon icon={faDiagramSubtask} />
												<Typography>statusMapping</Typography>
											</S.MenuItem>
										)}
									</Authorization>
									<S.MenuItem
										onContextMenu={() => handleRemove(closePopper)}
										onClick={() => handleRemove(closePopper)}>
										<Icon icon={faTrashCan} color="var(--red)" />
										<Typography color="var(--red)">remove</Typography>
									</S.MenuItem>
								</S.Menu>
							)}
						</Popper>
					)}
				</S.RowCenteredActions>
			</S.RowSpaced>
			{storeSyncInfo?.lastSyncDate && (
				<S.SyncInfo>
					<S.LastSyncInfo>
						<Icon icon={faClock} color="var(--green)" />
						<Typography variant="p3" color="var(--green)">
							<Trans
								i18nKey="lastSynchronized"
								values={{
									date: getLocaleDateWithTime(storeSyncInfo?.lastSyncDate),
									orderCount: storeSyncInfo?.orderCount
								}}
							/>
						</Typography>
					</S.LastSyncInfo>
				</S.SyncInfo>
			)}
			{storeLastError?.lastErrorDate && (
				<S.SyncInfo>
					<div className="info-content">
						<S.LastSyncInfo>
							<Icon icon={faTriangleExclamation} color="var(--red)" />
							<Typography variant="p3" color="var(--red)">
								<Trans
									i18nKey="lastErrorDate"
									values={{
										date: getLocaleDateWithTime(storeLastError?.lastErrorDate)
									}}
								/>
							</Typography>
						</S.LastSyncInfo>
					</div>

					{storeLastError?.lastErrorDate && (
						<Button
							label="reConnect"
							variant="outline"
							size="small"
							onClick={reConnectSalla}
							icon={faRotate}
							loading={sallaSyncLoading}
						/>
					)}
				</S.SyncInfo>
			)}
		</S.StoreBoxContainer>
	);
};

StoreBox.propTypes = {
	store: PropTypes.shape({
		ID: PropTypes.number.isRequired,
		storeName: PropTypes.string.isRequired,
		saleChannelID: PropTypes.number.isRequired,
		integrationName: PropTypes.string.isRequired,
		active: PropTypes.bool.isRequired,
		icon: PropTypes.string,
		statusMappingUpdateTypes: PropTypes.string,
		statusMapping: PropTypes.bool,
		storeLocation: PropTypes.bool,
		syncOrders: PropTypes.bool,
		syncProducts: PropTypes.bool
	}).isRequired
};

export default ConnectedStores;
